/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/interactive-supports-focus */
import { useEffect, useMemo, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import SVG from 'react-inlinesvg';
import chevronDownSvg from 'assets/icons/arrow_light_down.svg';

import { fetchDirectSalesProductFolder } from 'modules/api/directSalesProductTemplate';
import { InputContainer } from 'components/common/Deal/CampaignHeader/CampaignDrawer/Details/utils';
import { Label } from 'lib/Label';

import { cancelFunctions } from 'utils/cancelFunctions';

import { InputTheme, InputType } from 'components/common/types/Input.types';
import { Store } from 'components/common/types/Store.types';
import { ArrowContainer, Popover } from 'react-tiny-popover';
import saveSvg from 'assets/icons/save.svg';
import closeSvg from 'assets/icons/close.svg';
import { changeSelectedFolder } from 'store/productManagement/reducer';
import { BaseInput } from 'lib/BaseInput';
import IconButton from 'lib/IconButton';
import { ButtonSize, Theme } from 'lib/IconButton/IconButton.types';
import Search from 'lib/Search';
import { getIsReadOnly } from 'store/productManagement/selectors';
import { getMarketId } from 'store/publisher/selectors';

export const FolderInput: React.FC = () => {
  const dispatch = useDispatch();
  const marketId = useSelector(getMarketId);
  const selectedFolderId = useSelector((state: Store) => state.productManagement.newProduct.folder);
  const isReadOnly = useSelector(getIsReadOnly);

  const [folders, setFolders] = useState<string[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [isCreatingNewFolder, setIsCreatingNewFolder] = useState(false);

  const listRef = useRef<HTMLDivElement>(null);
  const activeLineRef = useRef<HTMLDivElement>(null);

  const filteredFolders = useMemo(() => {
    if (filterText.length < 1) return folders;
    return folders.filter((folder) => folder.toLowerCase().includes(filterText.toLowerCase()));
  }, [folders, filterText]);

  const onFolderSelect = (selectedFolder: string): void => {
    if (selectedFolder !== selectedFolderId) dispatch(changeSelectedFolder(selectedFolder));
    setIsOpen(false);
  };

  const handleTextChange = (textInput: string): void => {
    if (selectedFolderId && textInput !== selectedFolderId) {
      dispatch(changeSelectedFolder(''));
    }
    setFilterText(textInput);
  };

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const response = await fetchDirectSalesProductFolder(marketId, cancelFunctions);
        if (isMounted) {
          setFolders(response.map(({ folder }) => folder).filter((folder) => !!folder));
        }
        // eslint-disable-next-line no-empty
      } catch {}
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    setFilterText(selectedFolderId);
  }, [selectedFolderId, isOpen]);

  const clearNewFolderField = (): void => {
    if (!isCreatingNewFolder && !newFolderName) return;
    if (isCreatingNewFolder) dispatch(changeSelectedFolder(''));

    setNewFolderName('');
    setIsCreatingNewFolder(false);
  };

  return (
    <InputContainer>
      <Label id="folder" theme={InputTheme.FLAT_GRAY} label="Folder *" />
      <div data-test-id="folder-select" className="relative">
        <Popover
          containerClassName="z-30 py-1"
          align="end"
          positions={['bottom']}
          isOpen={isOpen}
          onClickOutside={() => {
            setIsOpen(false);

            if (!isCreatingNewFolder && newFolderName) setNewFolderName('');
          }}
          content={({ position, childRect, popoverRect }) => (
            <ArrowContainer
              position={position}
              childRect={childRect}
              popoverRect={popoverRect}
              arrowColor="var(--transparent-color)"
              arrowSize={0}
            >
              <div className="relative w-72 bg-neutral-50 shadow-md border border-neutral-300 rounded-md">
                <div className="flex flex-col">
                  <div data-test-id="line-select-list" ref={listRef} className="max-h-[500px] overflow-y-auto">
                    {!isCreatingNewFolder && (
                      <div className="my-2.5 mx-2.5">
                        <Search
                          value={filterText}
                          className="w-full"
                          placeholder="Search folders..."
                          onChange={handleTextChange}
                        />
                      </div>
                    )}
                    <div
                      className={cx(
                        'w-full flex justify-evenly items-center space-x-1 py-2.5 border-t border-neutral-300',
                        {
                          'border-b': filteredFolders.length > 0,
                        },
                      )}
                    >
                      <BaseInput
                        type={InputType.TEXT}
                        value={newFolderName}
                        className="w-full"
                        placeholder="Create new folder"
                        onChange={(event) => setNewFolderName(event.target.value)}
                      />
                      <div className="flex justify-evenly items-center space-x-1">
                        <IconButton
                          buttonSize={ButtonSize.LARGE}
                          theme={Theme.PRIMARY}
                          name="Save"
                          icon={saveSvg}
                          handleOnClick={() => {
                            setIsCreatingNewFolder(true);
                            onFolderSelect(newFolderName);
                          }}
                          isDisabled={
                            !newFolderName || folders.includes(newFolderName) || selectedFolderId === newFolderName
                          }
                        />
                        <IconButton
                          buttonSize={ButtonSize.LARGE}
                          theme={Theme.TRANSPARENT}
                          name="Close"
                          icon={closeSvg}
                          handleOnClick={clearNewFolderField}
                        />
                      </div>
                    </div>
                    {!isCreatingNewFolder && filteredFolders.length !== 0 && (
                      <div className="p-2.5">
                        {filteredFolders.map((folder) => {
                          return (
                            <div
                              ref={folder === selectedFolderId ? activeLineRef : null}
                              data-test-id={`option-${folder}`}
                              key={`option-${folder}`}
                              className={cx('w-full flex justify-start items-center space-x-2 px-2.5 rounded-md', {
                                'bg-neutral-950-opacity-5 sub-header-base text-primary': folder === selectedFolderId,
                                'hover:bg-primary-600-opacity-5 hover:text-primary': folder !== selectedFolderId,
                              })}
                            >
                              <button
                                className="py-2 w-full flex justify-between items-center body-base space-x-2 [&_svg]:fill-current [&_svg]:text-neutral-700"
                                type="button"
                                onClick={() => onFolderSelect(folder)}
                              >
                                <p className="w-10/12 text-left body-base truncate whitespace-nowrap overflow-hidden">
                                  {folder}
                                </p>
                              </button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </ArrowContainer>
          )}
        >
          <div
            role="button"
            onClick={() => {
              if (isCreatingNewFolder) setNewFolderName(selectedFolderId);
              !isReadOnly && setIsOpen(!isOpen);
            }}
            className={cx(
              'flex justify-between items-center px-2 py-1 sub-header-base border text-primary shadow-inner-sm rounded-md',
              {
                'border-primary-600 ring-2 ring-primary-600': isOpen,
                'border-neutral-300': !isOpen,
              },
            )}
          >
            <span className="body-base text-neutral-950-opacity-50 select-none">
              {selectedFolderId || 'Select a folder'}
            </span>
            <SVG src={chevronDownSvg} className={cx('w-4 h-4', { 'rotate-180': isOpen })} />
          </div>
        </Popover>
      </div>
    </InputContainer>
  );
};
/* eslint-enable jsx-a11y/interactive-supports-focus */
/* eslint-enable jsx-a11y/click-events-have-key-events */
